import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { donemHesapla, oncekiDonemHesapla, satisDonemiMi, type Donem, type SatisDonemi } from './donem';
import { kanalNormalize } from './kanal';

export interface SatisFiltresi {
  donemTuru: SatisDonemi;
  ozelAralik: { bas: string; bit: string } | null;
  /** null = tum subeler. Listede olup olmadigi SAYFADA denetlenir (liste 040'tan asenkron gelir). */
  sube: string | null;
  kanal: string | null;
}

export const VARSAYILAN_DONEM: SatisDonemi = 'aylik';

function gunMu(x: string | null): x is string {
  return x !== null && /^\d{4}-\d{2}-\d{2}$/.test(x) && dayjs(x).format('YYYY-MM-DD') === x;
}

/** URL -> filtre. Gecersiz her deger varsayilana duser (G2/K3); eski kanal gruplari E-Ticaret'e cevrilir (G2b). */
export function filtreOku(sp: URLSearchParams): SatisFiltresi {
  const d = sp.get('donem');
  const donemTuru: SatisDonemi = satisDonemiMi(d) ? d : VARSAYILAN_DONEM;
  const bas = sp.get('bas');
  const bit = sp.get('bit');
  const ozelAralik =
    donemTuru === 'ozel' && gunMu(bas) && gunMu(bit) && bas <= bit ? { bas, bit } : null;
  const sube = (sp.get('sube') ?? '').trim();
  const kanal = sp.get('kanal');
  return {
    donemTuru,
    ozelAralik,
    sube: sube === '' ? null : sube,
    kanal: kanalNormalize(kanal),
  };
}

/** Filtre degisikligi -> yeni URL. Dokunulmayan alanlar korunur; null = anahtar silinir. */
export function filtreYaz(onceki: URLSearchParams, d: Partial<SatisFiltresi>): URLSearchParams {
  const sp = new URLSearchParams(onceki);
  if (d.donemTuru !== undefined) {
    sp.set('donem', d.donemTuru);
    if (d.donemTuru !== 'ozel') {
      sp.delete('bas');
      sp.delete('bit');
    }
  }
  if (d.ozelAralik !== undefined) {
    if (d.ozelAralik) {
      sp.set('donem', 'ozel');
      sp.set('bas', d.ozelAralik.bas);
      sp.set('bit', d.ozelAralik.bit);
    } else {
      sp.delete('bas');
      sp.delete('bit');
    }
  }
  if (d.sube !== undefined) {
    if (d.sube) sp.set('sube', d.sube);
    else sp.delete('sube');
  }
  if (d.kanal !== undefined) {
    if (d.kanal) sp.set('kanal', d.kanal);
    else sp.delete('kanal');
  }
  return sp;
}

export interface SatisAnalizFiltresi extends SatisFiltresi {
  donem: Donem;
  oncekiDonem: Donem;
  degistir: (d: Partial<SatisFiltresi>) => void;
}

/**
 * Satis Analiz filtresinin TEK durumu URL'dir: yenileme ve paylasilan baglanti ayni gorunumu acar.
 * `replace: true` — her sekme tiklamasi tarayici gecmisine satir eklemesin.
 */
export function useSatisAnalizFiltre(): SatisAnalizFiltresi {
  const [sp, setSp] = useSearchParams();
  const anahtar = sp.toString();
  const filtre = useMemo(() => filtreOku(new URLSearchParams(anahtar)), [anahtar]);

  // Spec §3 (final-fix F-3b): gecerli bas/bit cifti olmayan Ozel, DONEM olarak varsayilandir (Aylik) —
  // aralik da K10 onceki pencere kurali da Aylik'tan. Sekme 'ozel' kalir (aralik secici ona bagli).
  const hesapTuru: SatisDonemi =
    filtre.donemTuru === 'ozel' && !filtre.ozelAralik ? VARSAYILAN_DONEM : filtre.donemTuru;

  const donem = useMemo<Donem>(() => {
    if (filtre.ozelAralik) {
      const { bas, bit } = filtre.ozelAralik;
      return { bas, bit, tekGun: bas === bit };
    }
    return donemHesapla(hesapTuru);
  }, [filtre.ozelAralik, hesapTuru]);

  const oncekiDonem = useMemo<Donem>(
    () => ({ ...oncekiDonemHesapla(donem, hesapTuru), tekGun: donem.tekGun }),
    [donem, hesapTuru],
  );

  const degistir = useCallback(
    (d: Partial<SatisFiltresi>) => setSp((onceki) => filtreYaz(onceki, d), { replace: true }),
    [setSp],
  );

  return { ...filtre, donem, oncekiDonem, degistir };
}
